import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthState, useAuth } from "../../context/AuthContext";
import { useApp } from "../../context/AppContext";
import customFetch from "../../api/customFetch";
import SlidesPager from "../../components/SlidesPager";
import LearningShell from "../../layouts/LearningShell";

// Loading component
const LoadingScreen = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      background: "var(--bg)",
      color: "var(--text)",
    }}
  >
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: "1.2rem", marginBottom: "10px" }}>טוען...</div>
      <div style={{ color: "var(--text)" }}>טוען נתוני קורס</div>
    </div>
  </div>
);

// רכיב התראה לקורס שבוצע לאחרונה
const RecentCompletionWarning = ({
  course,
  progress,
  onContinue,
  onGoBack,
}) => (
  <div
    style={{
      background: "var(--card)",
      padding: "24px",
      borderRadius: "16px",
      border: "2px solid #f39c12",
      textAlign: "center",
      direction: "rtl",
    }}
  >
    <div
      style={{
        fontSize: "2rem",
        marginBottom: "16px",
      }}
    >
      ⚠️
    </div>

    <h3
      style={{
        color: "#f39c12",
        marginTop: 0,
        marginBottom: "16px",
      }}
    >
      שים לב - קורס זה כבר בוצע
    </h3>

    <div
      style={{
        color: "var(--text)",
        marginBottom: "20px",
        lineHeight: "1.6",
      }}
    >
      <p>ביצעת את הקורס "{course?.title}" בתאריך:</p>
      <p
        style={{
          fontWeight: "bold",
          color: "var(--accent-one)",
          fontSize: "1.1rem",
        }}
      >
        {new Date(progress?.completedAt).toLocaleDateString("he-IL")}
      </p>
      <p>
        הציון שלך:{" "}
        <span
          style={{
            color: progress?.passed ? "#27ae60" : "#e74c3c",
            fontWeight: "bold",
          }}
        >
          {progress?.score}% ({progress?.passed ? "עבר" : "נכשל"})
        </span>
      </p>
    </div>

    <div
      style={{
        background: "#fff3cd",
        padding: "16px",
        borderRadius: "12px",
        marginBottom: "20px",
        color: "#856404",
      }}
    >
      האם אתה בטוח שברצונך לבצע את הקורס שוב?
      <br />
      הביצוע החדש יחליף את התוצאות הקודמות.
    </div>

    <div>
      <button
        onClick={onGoBack}
        style={{
          background: "var(--text)",
          color: "var(--bg)",
          border: "none",
          padding: "12px 20px",
          borderRadius: "16px",
          cursor: "pointer",
          fontSize: "1rem",
          fontWeight: "700",
          marginLeft: "12px",
        }}
      >
        חזור לדשבורד
      </button>

      <button
        onClick={onContinue}
        style={{
          background: "#f39c12",
          color: "white",
          border: "none",
          padding: "12px 20px",
          borderRadius: "16px",
          cursor: "pointer",
          fontSize: "1rem",
          fontWeight: "700",
        }}
      >
        בצע שוב בכל זאת
      </button>
    </div>
  </div>
);

// רכיב המשכת קורס שנעצר באמצע
const ResumeProgress = ({ course, progress, onResume, onRestart }) => (
  <div
    style={{
      background: "var(--card)",
      padding: "24px",
      borderRadius: "16px",
      border: "2px solid var(--accent)",
      textAlign: "center",
      direction: "rtl",
    }}
  >
    <div
      style={{
        fontSize: "2rem",
        marginBottom: "16px",
      }}
    >
      📚
    </div>

    <h3
      style={{
        color: "var(--accent)",
        marginTop: 0,
        marginBottom: "16px",
      }}
    >
      ברוכים השבים!
    </h3>

    <div
      style={{
        color: "var(--text)",
        marginBottom: "20px",
        lineHeight: "1.6",
      }}
    >
      <p>יש לך התקדמות שמורה בקורס "{course?.title}"</p>
      <p
        style={{
          fontWeight: "bold",
          color: "#ffffff",
          fontSize: "1.1rem",
        }}
      >
        התקדמות: שקף {progress?.currentSlideIndex + 1} מתוך{" "}
        {course?.slides?.length || 0}
      </p>
    </div>

    <div>
      <button
        onClick={onResume}
        style={{
          background: "var(--accent)",
          color: "#0b1c2b",
          border: "none",
          padding: "12px 20px",
          borderRadius: "16px",
          cursor: "pointer",
          fontSize: "1rem",
          fontWeight: "700",
          marginLeft: "12px",
        }}
      >
        המשך מהמקום שעצרת
      </button>

      <button
        onClick={onRestart}
        style={{
          background: "var(--text)",
          color: "var(--bg)",
          border: "none",
          padding: "12px 20px",
          borderRadius: "16px",
          cursor: "pointer",
          fontSize: "1rem",
          fontWeight: "700",
        }}
      >
        התחל מחדש
      </button>
    </div>
  </div>
);

export default function LandingInfo() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { isLoggedIn, user, loading } = useAuthState();
  const { logout } = useAuth();
  const { state, dispatch } = useApp();
  const [progress, setProgress] = useState(null);
  const [checkingProgress, setCheckingProgress] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const [showResume, setShowResume] = useState(false);

  useEffect(() => {
    if (loading) return;

    if (!isLoggedIn) {
      nav("/user/login");
      return;
    }

    // טעינת הקורס ובדיקת התקדמות
    (async () => {
      try {
        // טעינת הקורס
        if (!state.course || state.course.slug !== slug) {
          dispatch({ type: "RESET_QUIZ" }); // איפוס לפני טעינה
          const { data } = await customFetch.get(`/courses/${slug}`);
          dispatch({ type: "LOAD_COURSE", payload: data });
        }

        // בדיקת התקדמות קיימת
        const { data: progressData } = await customFetch.get(
          `/progress/${slug}`
        );
        console.log("Progress data received:", progressData);
        setProgress(progressData.progress);

        // בדיקה אם הקורס הושלם לאחרונה
        if (progressData.wasRecentlyCompleted) {
          setShowWarning(true);
        }
        // בדיקה אם יש התקדמות חלקית - רק אם יש התקדמות ממשית
        else if (
          progressData.progress &&
          !progressData.progress.isFirstTime &&
          progressData.progress.hasStarted
        ) {
          setShowResume(true);
        }

        setCheckingProgress(false);
      } catch (error) {
        console.error("Error loading course or progress:", error);
        setCheckingProgress(false);
        if (error.response?.status === 401) {
          await logout();
          nav("/user/login");
        }
      }
    })();
  }, [slug, nav, state.course, dispatch, isLoggedIn, loading, logout]);

  // פונקציות לטיפול בהחלטות המשתמש
  const handleContinueAfterWarning = async () => {
    setShowWarning(false);
    await startNewProgress();
  };

  const handleGoBackToDashboard = () => {
    nav("/user/dashboard");
  };

  const handleResumeProgress = () => {
    setShowResume(false);
    // המשך מהשקף הנוכחי
    dispatch({
      type: "SET_CURRENT_SLIDE",
      payload: progress.currentSlideIndex || 0,
    });
  };

  const handleRestartProgress = async () => {
    setShowResume(false);
    await startNewProgress();
  };

  const startNewProgress = async () => {
    try {
      // איפוס התקדמות בשרת
      await customFetch.delete(`/progress/${slug}`);

      // איפוס מצב האפליקציה
      dispatch({ type: "RESET" });
    } catch (error) {
      console.error("Error starting new progress:", error);
    }
  };

  // פונקציה שקובעת לאן להפנות אחרי סיום השקפים
  const handleSlidesFinish = async () => {
    try {
      // עדכון התקדמות לפני המעבר הבא
      await customFetch.put(`/progress/${slug}`, {
        currentSlideIndex: state.course.slides?.length || 0,
      });

      const course = state.course;
      const hasVideo = course?.videoUrl && course.videoUrl.trim() !== "";
      const hasQuestions = course?.questions && course.questions.length > 0;

      console.log("Course navigation decision:", {
        hasVideo,
        hasQuestions,
        videoUrl: course?.videoUrl,
      });

      if (hasVideo) {
        // יש סרטון - מעבר לדף הוידאו
        nav(`/${slug}/video`);
      } else if (hasQuestions) {
        // אין סרטון אבל יש שאלות - מעבר ישר לשאלון
        nav(`/${slug}/quiz`);
      } else {
        // אין סרטון ואין שאלות - הקורס הושלם
        nav("/user/dashboard");
      }
    } catch (error) {
      console.error("Error updating progress:", error);
      // במקרה של שגיאה, נמשיך בכל זאת
      const hasVideo =
        state.course?.videoUrl && state.course.videoUrl.trim() !== "";
      const hasQuestions =
        state.course?.questions && state.course.questions.length > 0;

      if (hasVideo) {
        nav(`/${slug}/video`);
      } else if (hasQuestions) {
        nav(`/${slug}/quiz`);
      } else {
        nav("/user/dashboard");
      }
    }
  };

  if (loading || checkingProgress) {
    return <LoadingScreen />;
  }

  if (!isLoggedIn) {
    return null;
  }

  // הצגת התראה לקורס שבוצע לאחרונה
  if (showWarning) {
    return (
      <LearningShell>
        <RecentCompletionWarning
          course={state.course}
          progress={progress}
          onContinue={handleContinueAfterWarning}
          onGoBack={handleGoBackToDashboard}
        />
      </LearningShell>
    );
  }

  // הצגת אפשרות להמשיך התקדמות
  if (showResume) {
    return (
      <LearningShell>
        <ResumeProgress
          course={state.course}
          progress={progress}
          onResume={handleResumeProgress}
          onRestart={handleRestartProgress}
        />
      </LearningShell>
    );
  }

  return (
    <LearningShell>
      {state.course && (
        <>
          {/* הצגת שם המשתמש בראש הדף */}
          <div
            style={{
              background: "var(--card)",
              padding: "16px 20px",
              borderRadius: "16px",
              marginBottom: "20px",
              textAlign: "right",
              direction: "rtl",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ fontSize: "0.9rem", color: "var(--text)" }}>
              לומד: {user?.fullName || user?.username}
            </div>
            <div
              style={{
                fontSize: "1.1rem",
                fontWeight: "bold",
                color: "var(--text)",
                marginTop: "15px",
                // color: "#ffffff",
              }}
            >
              {state.course.title}
            </div>
          </div>

          <SlidesPager
            slides={state.course.slides || []}
            onFinish={handleSlidesFinish}
          />
        </>
      )}
    </LearningShell>
  );
}
